import { useEffect, useState } from 'react'
import { BookOpen, ChevronRight, FlaskConical } from 'lucide-react'
import type { ExitKind, JournalStats, JournalTrade, TradeExcursion, WhatIfSummary, WhatIfTrade } from '@shared/journal'
import { workersApi } from '../../lib/api'
import { fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'
import { usd } from './WorkerCard'

const EXIT_LABEL: Record<ExitKind | 'time' | 'data end', string> = {
  target: 'target',
  stall: 'stall exit',
  stop: 'stop',
  raisedStop: 'raised stop',
  reduce: 'partial close',
  close: 'closed by a rule',
  manual: 'stopped by you',
  open: 'still held',
  time: 'time exit',
  'data end': 'still open (data end)',
}

const day = (t: number) => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' })
const tone = (n: number) => (n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-fg-muted')

function Stat({ label, value, sub, t }: { label: string; value: string; sub?: string; t?: number }) {
  return (
    <div className="min-w-0 rounded-xl border border-[var(--line-soft)] px-3.5 py-3">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={cn('num mt-1 truncate text-[20px] font-semibold', t != null ? tone(t) : 'text-fg')}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-fg-subtle">{sub}</div>}
    </div>
  )
}

/** "target 6 · stop 4 · raised stop 2" — shares by how they left. */
function exitMix(byExit: JournalStats['byExit']): string {
  return (Object.entries(byExit) as [ExitKind, number][])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${EXIT_LABEL[k]} ${n}`)
    .join(' · ')
}

function WhatIfTable({ rows, actual }: { rows: { label: string; net: number; delta: number; extra?: string }[]; actual?: number }) {
  return (
    <table className="w-full text-[12px]">
      <thead>
        <tr className="text-left text-[10.5px] text-fg-faint">
          <th className="pb-1 font-medium">If…</th>
          <th className="pb-1 text-right font-medium">Net</th>
          <th className="pb-1 text-right font-medium">vs the plan</th>
        </tr>
      </thead>
      <tbody className="num">
        {actual != null && (
          <tr className="border-t border-[var(--separator)] text-fg-muted">
            <td className="py-1">What really happened (IBKR)</td>
            <td className={cn('py-1 text-right font-medium', tone(actual))}>{usd(actual, true)}</td>
            <td className="py-1 text-right text-fg-faint">—</td>
          </tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} className={cn('border-t border-[var(--separator)]', i === 0 ? 'text-fg' : 'text-fg-muted')}>
            <td className="py-1">
              {r.label}
              {r.extra && <span className="ml-1 text-[10.5px] text-fg-faint">{r.extra}</span>}
            </td>
            <td className={cn('py-1 text-right font-medium', tone(r.net))}>{usd(r.net, true)}</td>
            <td className={cn('py-1 text-right', i === 0 ? 'text-fg-faint' : tone(r.delta))}>{i === 0 ? '—' : usd(r.delta, true)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TradeDetail({ t }: { t: JournalTrade }) {
  const [ex, setEx] = useState<TradeExcursion | { error: string } | null>(null)
  const [wi, setWi] = useState<WhatIfTrade | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    workersApi.excursion(t.id).then(setEx, (e) => setEx({ error: e instanceof Error ? e.message : 'failed' }))
  }, [t.id])
  const long = t.side === 'long'
  const runWhatIf = async () => {
    setBusy(true)
    setErr(null)
    try {
      const r = await workersApi.whatIf([t.id])
      setWi(r.trades[0] ?? null)
      if (!r.trades.length) setErr('This trade can’t be replayed (only worker trades are).')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-3 px-4 pb-4 pt-1 text-[12px]">
      <table className="w-full">
        <thead>
          <tr className="text-left text-[10.5px] text-fg-faint">
            <th className="pb-1 font-medium">Lot</th>
            <th className="pb-1 text-right font-medium">Bought</th>
            <th className="pb-1 font-medium pl-4">How it left</th>
            <th className="pb-1 text-right font-medium">Result</th>
          </tr>
        </thead>
        <tbody className="num">
          {t.lots.map((l) => {
            const res = l.exits.reduce((a, x) => a + (long ? 1 : -1) * (x.price - l.entry) * x.shares, 0)
            const open = l.qty - l.exits.reduce((a, x) => a + x.shares, 0)
            return (
              <tr key={l.lot} className="border-t border-[var(--separator)] align-top text-fg-muted">
                <td className="py-1 text-fg-subtle">{l.lot}</td>
                <td className="py-1 text-right">
                  {l.qty} @ {fmtPrice(l.entry)}
                </td>
                <td className="py-1 pl-4">
                  {l.exits.map((x, i) => (
                    <span key={i} className="block">
                      {EXIT_LABEL[x.kind]} {x.shares} @ {fmtPrice(x.price)}
                      <span className="text-fg-faint"> · {day(x.at)}</span>
                    </span>
                  ))}
                  {open > 0 && <span className="block text-fg-faint">{open} still held</span>}
                  <span className="block text-[10.5px] text-fg-faint">
                    planned: stop {fmtPrice(l.stop)}
                    {l.target != null ? ` · target ${fmtPrice(l.target)}` : ' · no target'}
                  </span>
                </td>
                <td className={cn('py-1 text-right', tone(res))}>{usd(res, true)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="text-[11.5px] text-fg-subtle">
        Plan: stops {t.plan.stops.map((x) => fmtPrice(x)).join(' / ') || '—'} · targets {t.plan.targets.map((x) => fmtPrice(x)).join(' / ') || '—'} · insurance {t.plan.insurance}
        {t.plan.zone ? ` · buy zone ${t.plan.zone}` : ''} · IBKR fees {usd(t.fees)}
      </div>

      {ex && (
        <div className="rounded-lg border border-[var(--line-soft)] px-3 py-2 text-[11.5px] text-fg-muted">
          {'error' in ex ? (
            <span className="text-fg-faint">Price path: {ex.error}</span>
          ) : (
            <>
              <span className="block">
                Best while held {ex.mfe ? `${fmtPrice(ex.mfe.price)} (${usd(ex.mfe.perShare, true)}/sh${ex.mfe.r != null ? `, ${ex.mfe.r}R` : ''})` : '—'} · worst{' '}
                {ex.mae ? `${fmtPrice(ex.mae.price)} (${usd(ex.mae.perShare, true)}/sh${ex.mae.r != null ? `, ${ex.mae.r}R` : ''})` : '—'}
              </span>
              {t.closedAt != null && (
                <span className="block">
                  After it ended: best {long ? 'high' : 'low'} {ex.after5 != null ? fmtPrice(ex.after5) : '—'} within 5 sessions,{' '}
                  {ex.after10 != null ? fmtPrice(ex.after10) : '—'} within 10
                </span>
              )}
              <span className="block text-[10.5px] text-fg-faint">{ex.source}</span>
            </>
          )}
        </div>
      )}

      {t.exitNotes.length > 0 && (
        <ul className="space-y-0.5 text-[11px] text-fg-subtle">
          {t.exitNotes.map((n, i) => (
            <li key={i}>· {n}</li>
          ))}
        </ul>
      )}

      {t.source === 'worker' && (
        <div>
          {!wi ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void runWhatIf()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50"
            >
              <FlaskConical size={13} /> {busy ? 'Replaying on its 5-minute bars…' : 'What if… (this trade)'}
            </button>
          ) : wi.error ? (
            <div className="text-[11.5px] text-fg-faint">{wi.error}</div>
          ) : (
            <WhatIfTable actual={wi.actual} rows={wi.results.map((r) => ({ label: r.label, net: r.net, delta: r.delta }))} />
          )}
          {err && <div className="mt-1 text-[11.5px] text-down">{err}</div>}
        </div>
      )}
    </div>
  )
}

/** Every trade the user made through workers (and bracket chat orders): results, exits, and "what if" on the same bars. */
export function Journal() {
  const [data, setData] = useState<{ trades: JournalTrade[]; stats: JournalStats } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [all, setAll] = useState<{ trades: WhatIfTrade[]; summary: WhatIfSummary[] } | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    workersApi.journal().then(setData, (e) => setErr(e instanceof Error ? e.message : 'failed'))
  }, [])
  if (err) return <div className="px-4 py-8 text-center text-[12.5px] text-fg-subtle">{err}</div>
  if (!data) return <div className="h-32 animate-pulse" />
  if (!data.trades.length)
    return (
      <div className="flex flex-col items-center px-4 py-10 text-center">
        <BookOpen size={22} className="text-fg-faint" />
        <div className="mt-2 text-[13px] font-medium text-fg-muted">No trades yet</div>
        <div className="mt-1 max-w-md text-[12px] text-fg-subtle">
          Every worker (and bracket chat order) that buys lands here: what it made after IBKR’s fees, why each lot left, how far the price ran for and against
          you — and what other stop, insurance or hold choices would have done on the same bars.
        </div>
      </div>
    )
  const s = data.stats
  const runAll = async () => {
    setBusy(true)
    try {
      setAll(await workersApi.whatIf())
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-5">
        <Stat label="Trades" value={`${s.closed} / ${s.trades}`} sub="closed / all" />
        <Stat label="Win rate" value={s.winRate != null ? `${Math.round(s.winRate * 100)}%` : '—'} sub={`${s.wins} won`} />
        <Stat label="Net" value={usd(s.net, true)} sub="closed trades, after IBKR fees" t={s.net} />
        <Stat label="Average R" value={s.avgR != null ? `${s.avgR}R` : '—'} sub={s.avgNet != null ? `${usd(s.avgNet, true)} a trade` : undefined} t={s.avgR ?? undefined} />
        <Stat label="Best · worst" value={s.best != null ? usd(s.best, true) : '—'} sub={s.worst != null ? `worst ${usd(s.worst, true)}` : undefined} t={s.best ?? undefined} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--separator)] px-4 py-2.5">
        <span className="num text-[11.5px] text-fg-subtle">Shares by exit: {exitMix(s.byExit) || '—'}</span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void runAll()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50"
          title="Replays every worker trade on its own 5-minute bars with one choice changed"
        >
          <FlaskConical size={13} /> {busy ? 'Replaying…' : 'What if… (all trades)'}
        </button>
      </div>
      {all && (
        <div className="border-t border-[var(--separator)] px-4 py-3">
          <WhatIfTable rows={all.summary.map((x) => ({ label: x.label, net: x.net, delta: x.delta, extra: `${x.wins}/${x.trades} won` }))} />
          <p className="mt-1.5 text-[11px] text-fg-faint">
            Same entries, EODHD 5-minute bars (regular hours), pessimistic inside a bar, your IBKR fee plan. {all.summary[0]?.trades ?? 0} trade(s) — few trades are weak
            evidence.
          </p>
        </div>
      )}
      {data.trades.map((t) => (
        <div key={t.id} className="border-t border-[var(--separator)]">
          <button onClick={() => setOpen(open === t.id ? null : t.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--color-surface-2)]">
            <span className="num w-12 shrink-0 text-[11.5px] text-fg-faint">{day(t.openedAt)}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-fg">
                {t.symbol} <span className="font-normal text-fg-subtle">· {t.name}</span>
              </span>
              <span className="num mt-0.5 block truncate text-[11.5px] text-fg-subtle">
                {t.shares} sh @ {fmtPrice(t.avgEntry)}
                {t.avgExit != null ? ` → ${fmtPrice(t.avgExit)}` : ''} · {exitMix(Object.fromEntries(t.lots.flatMap((l) => l.exits).reduce((m, x) => m.set(x.kind, (m.get(x.kind) ?? 0) + x.shares), new Map<ExitKind, number>()))) || 'held'}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className={cn('num block text-[12.5px] font-medium', tone(t.net))}>{t.closedAt != null ? usd(t.net, true) : 'open'}</span>
              <span className="num block text-[10.5px] text-fg-faint">{t.r != null ? `${t.r}R` : t.source === 'order' ? 'chat order' : ''}</span>
            </span>
            <ChevronRight size={15} className={cn('shrink-0 text-fg-faint transition-transform', open === t.id && 'rotate-90')} />
          </button>
          {open === t.id && <TradeDetail t={t} />}
        </div>
      ))}
    </div>
  )
}
